import React, { useState, useEffect, useContext } from "react";
import { motion } from "framer-motion";
import { AuthContext } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Users, Wifi, WifiOff, MousePointer2, Edit3 } from "lucide-react";
import axios from "axios";
import { fadeInUp } from "@/config/animations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WS_URL = process.env.REACT_APP_BACKEND_URL?.replace("https://", "wss://").replace("http://", "ws://");

const CURSOR_COLORS = ["#06b6d4", "#8b5cf6", "#f59e0b", "#10b981", "#ef4444", "#3b82f6"];

export default function CollaborationPanel({ teamId, workflowId }) {
  const { user } = useContext(AuthContext);
  const [connected, setConnected] = useState(false);
  const [presenceUsers, setPresenceUsers] = useState([]);
  const [lockedTasks, setLockedTasks] = useState({});
  const [ws, setWs] = useState(null);
  const [editLog, setEditLog] = useState([]);

  useEffect(() => {
    if (!teamId || !user) return;

    const sessionToken = document.cookie.split(";").map(c => c.trim()).find(c => c.startsWith("session_token="))?.split("=")[1];
    if (!sessionToken) return;

    const socket = new WebSocket(`${WS_URL}/ws/${sessionToken}`);

    socket.onopen = () => {
      setConnected(true);
      socket.send(JSON.stringify({
        type: "cursor_update",
        team_id: teamId,
        name: user.name,
        page: "collaboration",
        color: CURSOR_COLORS[Math.floor(Math.random() * CURSOR_COLORS.length)],
      }));
    };

    socket.onmessage = (event) => {
      const msg = JSON.parse(event.data);
      if (msg.type === "presence_update") {
        setPresenceUsers(Object.entries(msg.users || {}).map(([uid, data]) => ({ userId: uid, ...data })));
      } else if (msg.type === "workflow_edit") {
        setEditLog(prev => [{
          operation: msg.operation,
          by: msg.by_user,
          timestamp: msg.timestamp,
          data: msg.data,
        }, ...prev].slice(0, 30));
      } else if (msg.type === "task_lock") {
        setLockedTasks(prev => ({
          ...prev,
          [msg.task_id]: msg.locked_by ? { user: msg.locked_by, name: msg.user_name, color: msg.color } : null,
        }));
      }
    };

    socket.onclose = () => setConnected(false);
    socket.onerror = () => setConnected(false);

    setWs(socket);
    return () => {
      if (socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ type: "leave_presence", team_id: teamId }));
      }
      socket.close();
    };
  }, [teamId, user]);

  const sendEdit = (operation, data) => {
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({
        type: "workflow_edit",
        workflow_id: workflowId,
        team_id: teamId,
        operation,
        data,
      }));
      setEditLog(prev => [{
        operation, by: user?.user_id || "you",
        timestamp: new Date().toISOString(), data,
      }, ...prev].slice(0, 30));
    }
  };

  const lockTask = (taskId, lock) => {
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({
        type: "task_lock",
        workflow_id: workflowId,
        task_id: taskId,
        team_id: teamId,
        lock,
        user_name: user?.name,
      }));
    }
  };

  return (
    <motion.div {...fadeInUp} className="space-y-4" data-testid="collaboration-panel" data-automation-id="collaboration-panel">
      {/* Connection Status */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {connected ? (
            <Badge className="bg-green-500/20 text-green-400 text-xs"><Wifi className="w-3 h-3 mr-1" /> Live</Badge>
          ) : (
            <Badge className="bg-red-500/20 text-red-400 text-xs"><WifiOff className="w-3 h-3 mr-1" /> Offline</Badge>
          )}
          <span className="text-sm text-[var(--qn-foreground-muted)]">{presenceUsers.length} users online</span>
        </div>
      </div>

      {/* Online Users */}
      {presenceUsers.length > 0 && (
        <Card className="card-themed">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="w-4 h-4" /> Active Collaborators
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {presenceUsers.map((u) => (
              <div key={u.userId} className="flex items-center gap-3 text-sm">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: u.color || "#06b6d4" }} />
                <span className="font-medium">{u.name || "Anonymous"}</span>
                {u.page && <Badge variant="outline" className="text-xs">{u.page}</Badge>}
                <MousePointer2 className="w-3 h-3 ml-auto text-[var(--qn-foreground-subtle)]" style={{ color: u.color }} />
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Edit Log */}
      <Card className="card-themed">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Edit3 className="w-4 h-4" /> Recent Edits
          </CardTitle>
        </CardHeader>
        <CardContent>
          {editLog.length === 0 ? (
            <p className="text-sm text-[var(--qn-foreground-muted)] text-center py-4">
              {connected ? "No edits yet. Changes will appear here in real-time." : "Connect to a team to see live edits."}
            </p>
          ) : (
            <div className="space-y-1.5 max-h-[250px] overflow-y-auto">
              {editLog.map((entry, i) => (
                <div key={i} className="flex items-center gap-2 text-sm bg-[var(--qn-background)] rounded px-2.5 py-1.5">
                  <Badge variant="outline" className="text-xs shrink-0">{entry.operation}</Badge>
                  <span className="text-[var(--qn-foreground-muted)] truncate">
                    {entry.data?.task_id || entry.data?.task?.name || "workflow"}
                  </span>
                  <span className="ml-auto text-xs text-[var(--qn-foreground-subtle)] shrink-0">
                    {new Date(entry.timestamp).toLocaleTimeString()}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Task Locks */}
      {Object.entries(lockedTasks).some(([_, v]) => v) && (
        <Card className="card-themed">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Locked Tasks</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            {Object.entries(lockedTasks).filter(([_, v]) => v).map(([tid, lock]) => (
              <div key={tid} className="flex items-center gap-2 text-sm">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: lock.color }} />
                <span>{tid}</span>
                <span className="text-[var(--qn-foreground-muted)]">locked by {lock.name}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Instructions */}
      {!teamId && (
        <Card className="card-themed">
          <CardContent className="p-6 text-center text-[var(--qn-foreground-muted)]">
            <Users className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">Select a team and workflow to start collaborating in real-time</p>
          </CardContent>
        </Card>
      )}
    </motion.div>
  );
}
